import {useTranslation} from "react-i18next";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {ErrorState, LessonOccurrenceDecorator} from "../../../../model/LessonOccurrenceDecorator";
import {TimetableDecorator} from "../../../../model/TimetableDecorator";
import {MONGO_DATE_FORMAT, nextOrSameDay} from "../../../../utils/date";
import {COLOR_DEFAULT} from "../../../../utils/color";
import {RecurringPatternDecorator} from "../../../../model/RecurringPatternDecorator";
import {SubjectDialogUpdateOptionsProps} from "../../../task/dialog/SubjectCommitDialog";
import {Subject} from "../../../../types/Subject";
import {Lesson} from "../../../../types/Lesson";
import {useLessonManager} from "../../../../hooks/useLessonManager";
import {useLessonOccurrenceManager} from "../../../../hooks/useLessonOccurrenceManager";
import {useSubjectManager} from "../../../../hooks/useSubjectManager";
import {LessonOccurrence} from "../../../../types/LessonOccurrence";
import ObjectID from "bson-objectid";
import moment from "moment/moment";

// 1. Define the props this hook will take
interface LogicProps {
    open: boolean;
    selectedPlannerId: string;
    selectedTimetable: TimetableDecorator;
    subjects: Subject[] | undefined;
    updateOptions?: {
        lesson: Lesson | undefined;
        occurrences: LessonOccurrenceDecorator[] | undefined;
    } | null;
}

// 2. Define everything this hook will return
export function useLessonCommitDialogLogic(props: LogicProps) {
    const {open, updateOptions, selectedPlannerId, selectedTimetable, subjects} = props;
    const {t} = useTranslation();

    // --- State ---
    const [occurrences, setOccurrences] = useState<LessonOccurrenceDecorator[]>([]);
    const [selectedSubjectId, setSelectedSubjectId] = useState<string | undefined | null>(null);
    const [title, setTitle] = useState<string | undefined | null>(null);
    const [stateColor, setColor] = useState<string | undefined | null>(null);
    const [useSubject, setUseSubject] = useState<boolean>(true);
    const [room, setRoom] = useState<string | undefined | null>(null);
    const [note, setNote] = useState<string | undefined | null>(null);
    const [error, setError] = useState<string | undefined>(undefined);

    // --- UI State for Popovers ---
    const [repeatDialogOccurrence, setRepeatDialogOccurrence] = useState<LessonOccurrenceDecorator | undefined | null>(undefined);
    const [subjectAnchorEl, setSubjectAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [subjectDialogOpen, setSubjectDialogOpen] = React.useState(false);
    const [colorAnchorEl, setColorAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [subjectDialogUpdateOptions, setSubjectDialogUpdateOptions] = useState<SubjectDialogUpdateOptionsProps | undefined | null>(null);

    // --- Memoize Hook Arguments ---
    const managerProps = useMemo(() => ({
        plannerId: selectedPlannerId,
        timetableId: selectedTimetable.getTimetableId(),
    }), [selectedPlannerId]);

    const {createLesson, updateLesson} = useLessonManager(managerProps);
    const {createLessonOccurrence, updateLessonOccurrencesForLesson} = useLessonOccurrenceManager(managerProps);
    const {deleteSubject} = useSubjectManager(managerProps);

    // --- Initialize State on Open ---
    useEffect(() => {
        if (open) {
            if (updateOptions) {
                setOccurrences(updateOptions.occurrences || []);
                setSelectedSubjectId(updateOptions.lesson?.subjectId);
                setTitle(updateOptions.lesson?.title);
                setColor(updateOptions.lesson?.color);
                setUseSubject(!!updateOptions.lesson?.subjectId);
                setRoom(updateOptions.lesson?.room);
                setNote(updateOptions.lesson?.note);
            } else {
                setOccurrences([new LessonOccurrenceDecorator(getDefaultOccurrence())]);
                setSelectedSubjectId(null);
                setTitle(null);
                setColor(COLOR_DEFAULT);
                setUseSubject(true);
                setRoom(null);
                setNote(null);
            }
        }
    }, [open, updateOptions]);

    // --- Derived State ---
    const color = stateColor ?? COLOR_DEFAULT;
    const selectedSubject: Subject | undefined = subjects?.find(it => it.id === selectedSubjectId);

    // --- Callbacks (Memoized) ---
    const validate_create_update = useCallback((): Promise<never> | undefined => {
        if (useSubject) {
            if (!selectedSubject) return Promise.reject(t("timetable.lesson.commit_dialog.error.subject_not_selected"));
        } else {
            if (!title) return Promise.reject(t("timetable.lesson.commit_dialog.error.subject_name_empty"));
        }
        if (occurrences.length === 0) return Promise.reject(t("timetable.lesson.commit_dialog.error.no_occurrences"));

        const errorStates: number[] = [];
        occurrences.forEach((occurrence) => errorStates.push(...occurrence.getErrorStatesIfExists(selectedTimetable)));
        if (errorStates.length > 0) {
            switch (errorStates[0]) {
                case ErrorState.INVALID_START_DATE:
                    return Promise.reject(t("timetable.lesson.commit_dialog.error.invalid_start_date"))
                case ErrorState.START_TIME_MISSING:
                    return Promise.reject(t("timetable.lesson.commit_dialog.error.start_time_not_specified"))
                case ErrorState.END_TIME_MISSING:
                    return Promise.reject(t("timetable.lesson.commit_dialog.error.end_time_not_specified"))
                case ErrorState.START_TIME_OUT_OF_BOUNDS:
                    return Promise.reject(t("timetable.lesson.commit_dialog.error.invalid_start_time"))
                case ErrorState.END_TIME_OUT_OF_BOUNDS:
                    return Promise.reject(t("timetable.lesson.commit_dialog.error.invalid_end_time"))
                case ErrorState.END_TIME_BEFORE_START:
                    return Promise.reject(t("timetable.lesson.commit_dialog.error.start_date_after_end_date"))
            }
        }
    }, [useSubject, selectedSubject, title, occurrences, selectedTimetable, t]);

    const createLessonCallback = useCallback(async () => {
        const result = validate_create_update();
        if (result) return result;
        const lessonId = await createLesson({
            timetableId: selectedTimetable.getTimetableId(),
            ...(useSubject && selectedSubject) && {subjectId: selectedSubject.id},
            ...(!useSubject && title) && {title: title},
            color: color,
            ...(room) && {room: room},
            ...(note) && {note: note}
        });
        if (!lessonId) return Promise.reject("Lesson creation failed");
        const occurrencePromises = occurrences.map(it => {
            const pattern = it.getRecurringPattern();
            return createLessonOccurrence(lessonId, {
                date: it.getDate()?.format(MONGO_DATE_FORMAT),
                indexOfDay: it.getIndexOfDay(),
                indexOfWeek: it.getIndexOfWeek(),
                timeStartInMinutes: it.getStartTimeInMinutes() ?? undefined,
                timeEndInMinutes: it.getEndTimeInMinutes() ?? undefined,
                timeStartInPeriods: it.getStartTimeInPeriod() ?? undefined,
                timeEndInPeriods: it.getEndTimeInPeriod() ?? undefined,
                isRecurring: it.isRecurring(),
                ...(pattern) && {recurringPattern: pattern},
                instanceExceptions: Array.from(it.getInstanceExceptions().values())
            });
        });
        await Promise.all(occurrencePromises);
    }, [validate_create_update, createLesson, selectedTimetable, useSubject, selectedSubject, title, color, room, note, occurrences, createLessonOccurrence]);

    const updateLessonCallback = useCallback(async () => {
        const lessonId = updateOptions?.lesson?.id;
        if (!lessonId) return Promise.reject("An unexpected error occurred");
        const result = validate_create_update();
        if (result) return result;

        const updateSubjectId = useSubject && selectedSubject ? selectedSubject.id : undefined;
        const updateSubjectTitle = !useSubject ? title : undefined;

        await updateLesson(lessonId, {
            ...(updateSubjectId) && {subjectId: updateSubjectId},
            ...(updateSubjectTitle) && {title: updateSubjectTitle},
            color: color,
            room: room ?? undefined,
            note: note ?? undefined
        });

        const optionsList = occurrences.map(it => {
            const pattern = it.getRecurringPattern();
            return {
                lessonId: lessonId,
                date: it.getDate()?.format(MONGO_DATE_FORMAT),
                indexOfDay: it.getIndexOfDay(),
                indexOfWeek: it.getIndexOfWeek(),
                timeStartInMinutes: it.getStartTimeInMinutes() ?? undefined,
                timeEndInMinutes: it.getEndTimeInMinutes() ?? undefined,
                timeStartInPeriods: it.getStartTimeInPeriod() ?? undefined,
                timeEndInPeriods: it.getEndTimeInPeriod() ?? undefined,
                isRecurring: it.isRecurring(),
                ...(pattern) && {recurringPattern: pattern}
            };
        });
        await updateLessonOccurrencesForLesson(lessonId, optionsList);
    }, [validate_create_update, updateOptions, useSubject, selectedSubject, title, color, room, note, updateLesson, occurrences, updateLessonOccurrencesForLesson]);

    const addOccurrence = useCallback(() => {
        const newOccurrences = occurrences.map(o => new LessonOccurrenceDecorator(o.toProps()));
        newOccurrences.push(new LessonOccurrenceDecorator(getDefaultOccurrence()));
        setOccurrences(newOccurrences);
    }, [occurrences]);

    const updateOccurrence = useCallback((occurrenceId: string, func: (occurrence: LessonOccurrenceDecorator) => void) => {
        const newOccurrences = occurrences.map(it => new LessonOccurrenceDecorator(it.toProps()));
        const occurrence = newOccurrences.find(it => it.getId() === occurrenceId);
        if (occurrence) {
            func(occurrence);
            setOccurrences(newOccurrences);
        }
    }, [occurrences]);

    const handleOnOccurrenceDelete = useCallback((occurrenceId: string) => {
        setOccurrences(occurrences.filter(it => it.getId() !== occurrenceId));
    }, [occurrences]);

    const getRepeatDialogPattern = useCallback(() => {
        const pattern = repeatDialogOccurrence?.getRecurringPattern();
        return pattern ? new RecurringPatternDecorator(pattern) : undefined;
    }, [repeatDialogOccurrence]);

    const getRepeatDialogIsRecurring = useCallback(() => {
        return repeatDialogOccurrence?.isRecurring();
    }, [repeatDialogOccurrence]);

    const handleOnRepeatChanged = useCallback((isRecurring: boolean, pattern?: RecurringPatternDecorator) => {
        updateOccurrence(repeatDialogOccurrence?.getId() || '', it => {
            it.setRecurring(isRecurring);
            it.setRecurringPattern(pattern?.get());
        });
    }, [repeatDialogOccurrence, updateOccurrence]);

    const handleDateChanged = useCallback((occurrenceId: string, date: moment.Moment) => {
        if (!date.isValid()) return;
        updateOccurrence(occurrenceId, it => it.setDate(date));
    }, [updateOccurrence]);

    const handleDayOfWeekChange = useCallback((occurrenceId: string, dayOfWeek: number) => {
        if (!(dayOfWeek >= 1 && dayOfWeek <= 7)) return;
        const date = nextOrSameDay(moment().startOf('day'), dayOfWeek);
        updateOccurrence(occurrenceId, it => it.setDate(date));
    }, [updateOccurrence]);

    const handleIndexOfWeekChange = useCallback((occurrenceId: string, indexOfWeek: number) => {
        if (!(indexOfWeek >= 0 && indexOfWeek < selectedTimetable.getNumberOfWeeks())) return;
        updateOccurrence(occurrenceId, it => it.setIndexOfWeek(indexOfWeek));
    }, [updateOccurrence, selectedTimetable]); // <-- Needs selectedTimetable

    const handleIndexOfDayChange = useCallback((occurrenceId: string, indexOfDay: number) => {
        if (!(indexOfDay >= 0 && indexOfDay < selectedTimetable.getShiftNumberOfDays())) return;
        updateOccurrence(occurrenceId, it => it.setIndexOfDay(indexOfDay));
    }, [updateOccurrence, selectedTimetable]); // <-- Needs selectedTimetable

    const handleStartTimeInMinutesChange = useCallback((occurrenceId: string, timeInMinutes: number) => {
        updateOccurrence(occurrenceId, it => it.setTimeStartInMinutes(timeInMinutes));
    }, [updateOccurrence]);

    const handleEndTimeInMinutesChange = useCallback((occurrenceId: string, timeInMinutes: number) => {
        updateOccurrence(occurrenceId, it => it.setTimeEndInMinutes(timeInMinutes));
    }, [updateOccurrence]);

    const handleStartTimeInPeriodsChange = useCallback((occurrenceId: string, timeInPeriods: number) => {
        updateOccurrence(occurrenceId, it => it.setTimeStartInPeriods(timeInPeriods));
    }, [updateOccurrence]);

    const handleEndTimeInPeriodsChange = useCallback((occurrenceId: string, timeInPeriods: number) => {
        updateOccurrence(occurrenceId, it => it.setTimeEndInPeriods(timeInPeriods));
    }, [updateOccurrence]);

    // --- Return all state and handlers ---
    return {
        // State
        occurrences,
        selectedSubjectId,
        selectedSubject,
        title,
        color,
        useSubject,
        room,
        note,
        error,

        // UI State
        repeatDialogOccurrence,
        subjectAnchorEl,
        subjectDialogOpen,
        colorAnchorEl,
        subjectDialogUpdateOptions,

        // Callbacks
        setTitle,
        setColor,
        setUseSubject,
        setRoom,
        setNote,
        setOccurrences,
        setSelectedSubjectId,
        setError,
        setRepeatDialogOccurrence,
        setSubjectAnchorEl,
        setSubjectDialogOpen,
        setColorAnchorEl,
        setSubjectDialogUpdateOptions,
        createLessonCallback,
        updateLessonCallback,
        addOccurrence,
        updateOccurrence,
        handleOnOccurrenceDelete,
        getRepeatDialogPattern,
        getRepeatDialogIsRecurring,
        handleOnRepeatChanged,
        deleteSubject,
        handleDateChanged,
        handleDayOfWeekChange,
        handleIndexOfWeekChange,
        handleIndexOfDayChange,
        handleStartTimeInMinutesChange,
        handleEndTimeInMinutesChange,
        handleStartTimeInPeriodsChange,
        handleEndTimeInPeriodsChange,
    };
}

// --- Helper for default ---
const getDefaultOccurrence = (): LessonOccurrence => {
    return {
        id: ObjectID().toHexString(),
        plannerId: "",
        timetableId: "",
        lessonId: "",
        date: moment().format(MONGO_DATE_FORMAT),
        indexOfWeek: 0,
        indexOfDay: 0,
        timeStartInMinutes: 9 * 60,
        timeEndInMinutes: 10 * 60,
        timeStartInPeriods: 1,
        timeEndInPeriods: 2,
        isRecurring: true,
        recurringPattern: undefined,
        instanceExceptions: []
    }
}